import { useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import { OPEN_TASK_STATUSES, type Task } from "@recobra/schemas/task";

import { useAuth } from "../../lib";
import { getErrorMessage } from "../../lib/api";
import { useUpdateTaskMutation } from "../task-data";

export type TaskNotesSaveStatus = "idle" | "saving" | "saved" | "error";

type TaskNotesDraft = { notes: string; revision: number };

export const useTaskNotes = (task: Task) => {
  const queryClient = useQueryClient();
  const draftKey = useMemo(() => ["task-notes-draft", task.id], [task.id]);
  const { user } = useAuth();
  const [draft, setDraft] = useState<TaskNotesDraft | null>(
    () => queryClient.getQueryData<TaskNotesDraft>(draftKey) ?? null,
  );
  const [saveStatus, setSaveStatus] = useState<TaskNotesSaveStatus>("idle");
  const revision = useRef(draft?.revision ?? 0);
  const mounted = useRef(false);
  const inFlight = useRef<number | null>(null);
  const { mutate } = useUpdateTaskMutation({
    username: user.username,
    contextType: task.contextType,
    contextId: task.contextId,
    onSuccess: ({ notesRevision }) => {
      if (queryClient.getQueryData<TaskNotesDraft>(draftKey)?.revision !== notesRevision) return;
      queryClient.removeQueries({ queryKey: draftKey, exact: true });
      if (mounted.current) setSaveStatus("saved");
    },
    onError: (error, { notesRevision }) => {
      if (queryClient.getQueryData<TaskNotesDraft>(draftKey)?.revision !== notesRevision) return;
      if (mounted.current) setSaveStatus("error");
      else toast.error(m.task_update_error({ message: getErrorMessage(error) }));
    },
    onSettled: ({ notesRevision }) => {
      if (inFlight.current === notesRevision) inFlight.current = null;
    },
  });
  const save = useMemo(
    () =>
      debounce((pending: TaskNotesDraft) => {
        inFlight.current = pending.revision;
        mutate({
          id: task.id,
          input: { notes: pending.notes },
          silent: true,
          notesRevision: pending.revision,
        });
      }, 1000),
    [mutate, task.id],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const pending = queryClient.getQueryData<TaskNotesDraft>(draftKey);
      const status = queryClient.getQueryData<Task>(["tasks", task.id])?.status;
      const isClosed = status !== undefined && !OPEN_TASK_STATUSES.includes(status);
      if (isClosed) {
        queryClient.removeQueries({ queryKey: draftKey, exact: true });
        save.cancel();
      } else if (pending && pending.revision !== inFlight.current) {
        save(pending);
      }
      save.flush();
      save.cancel();
    };
  }, [save, queryClient, draftKey, task.id]);

  return {
    notes: draft?.notes ?? task.notes ?? "",
    setNotes: (notes: string) => {
      const pending = { notes, revision: ++revision.current };
      queryClient.setQueryDefaults(draftKey, { gcTime: Infinity });
      queryClient.setQueryData(draftKey, pending);
      setDraft(pending);
      setSaveStatus("saving");
      save(pending);
    },
    notesSaveStatus: saveStatus,
    cancelNotesSave: () => {
      save.cancel();
      setSaveStatus("idle");
    },
  };
};
